/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import {
  CentralManualCostState,
  CentralReferenceList,
  ManualCostSource,
  ReferenceMeasurement,
  getDisplayQuantity,
  getEstimateNumberValueBasedOnSettings,
  manualCostSources,
} from "@Actions/estimation";
import { useSelector } from "react-redux";
import { v4 as uuid } from "uuid";
import _ from "lodash";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import Text from "@CubeComponents/html/Text/Text";
import type { RootState } from "src/reducers";

import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import AddReferences from "./AddReferenceList";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { EstimateData } from "@ServerTypes/estimates/estimates.schema";
import type { Formatter } from "slickgrid-react";
const FormulaBuilder = lazy(() => import("./FormulaCalculator"));

type AddReferenceData = {
  id: string;
  name: string;
  value?: number;
  quantity: number;
  multiplier: number;
  unit: { factor: number };
  __isDummy?: boolean;
};

type SlickGridHandle<T> = {
  dataView: {
    getAllSelectedItems: () => T[];
  };
  slickGrid: {
    getSelectedRows: () => number[];
    getDataItem: (row: number) => T;
  };
};

type ComponentProps<T> = {
  estimateId: ObjectIdString;
  fileVersionId?: string;
  initialData?: CentralManualCostState;
  source: ManualCostSource;
  manualData: CentralManualCostState;
  setManualData: React.Dispatch<React.SetStateAction<T>>;
  headerText: TranslatedString | React.ReactElement;
  footerConfirmText: TranslatedString | React.ReactElement;
  footerCancelText: TranslatedString | React.ReactElement;
  upperContent?: () => React.ReactElement;
  referenceToggleLabel: TranslatedString | React.ReactElement;
  noReferenceHeading1: TranslatedString | React.ReactElement;
  noReferenceHeading2: TranslatedString | React.ReactElement;
  addReferenceHeaderText: TranslatedString | React.ReactElement;
  formulaDetailToolTip: TranslatedString;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
  updateValueField?: (value: null | number) => void;
  isSaveButtonDisabled: boolean;
};

function CentralManualCostComponent<T extends CentralManualCostState>({
  estimateId,
  fileVersionId,
  initialData,
  source,
  manualData,
  setManualData,
  headerText,
  footerConfirmText,
  footerCancelText,
  upperContent,
  referenceToggleLabel,
  noReferenceHeading1,
  noReferenceHeading2,
  addReferenceHeaderText,
  formulaDetailToolTip,
  handleSave,
  handleCancel,
  updateValueField,
  isSaveButtonDisabled,
}: ComponentProps<T>) {
  const { t } = useTranslation();
  const [toolButtonState, setToolButtonState] = useState({ add: false, remove: true });
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateData;
  const [openReferenceModal, setOpenReferenceModal] = useState(false);
  const [addReferenceButtonState, setAddReferenceButtonState] = useState(false);
  const [isAnyActiveAction, setIsAnyActiveAction] = useState(false);
  const [variable, setVariable] = useState<{ [id: string]: string }>({});

  const addReferenceRef = useRef<SlickGridHandle<AddReferenceData> | null>(null);

  const referenceGrid = useRef<SlickGridHandle<CentralReferenceList> | null>(null);
  const variableRef = useRef<{ [id: string]: string }>(variable);
  useEffect(() => {
    variableRef.current = variable;
  }, [variable]);

  useEffect(() => {
    if (initialData?.references) {
      const obj: { [id: string]: string } = {};
      initialData.references.map((val: CentralReferenceList) => {
        obj[val.id] = val.variable ?? "";
      });
      setVariable(obj);
    }
  }, [initialData]);

  const toolbarOptions = useMemo(
    () => [
      {
        id: "addReference",
        name: "Reference",
        disabled: toolButtonState.add,
      },
      {
        id: "deleteReference",
        name: "remove",
        prefixIcon: "delete",
        disabled: toolButtonState.remove,
      },
    ],
    [toolButtonState],
  );

  const variableChangeHandler = (params: { id: string }) => (e: { target: { value: string } }) => {
    const newValue = e.target.value.replace(/[^A-Za-z]/g, "");
    setVariable((prev) => ({ ...prev, [params.id]: newValue }));
    setManualData((prev) => {
      const updatedData = { ...prev };
      updatedData.references.map((val: CentralReferenceList) => {
        if (val.id == params.id) {
          val.variable = newValue;
        }
      });
      return updatedData;
    });
  };

  const setValueOfFormula = (value: number | null, exp: string) => {
    setManualData((prev) => ({ ...prev, value, formula: exp }));
    updateValueField && updateValueField(value);
  };

  const variableTemplate: Formatter<CentralReferenceList> = (_row, _cell, _value, _columnDef, params) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <TextFieldComponent
        defaultValue={variableRef.current[params.id]}
        onChange={variableChangeHandler(params)}
        onKeyDown={(e) => {
          if (e.key.length === 1 && !/[A-Za-z]/.test(e.key) && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
          }
        }}
        onPaste={(e) => {
          if (!/^[A-Za-z]*$/.test(e.clipboardData.getData("text"))) {
            e.preventDefault();
          }
        }}
      />,
    );

    return container;
  };

  const MeasurementTemplate: Formatter<ReferenceMeasurement> = (_row, _cell, _value, _columnDef, params) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(<div>{getEstimateNumberValueBasedOnSettings(params.measurement, estimateData, "measurement")}</div>);
    return container;
  };

  const handleReferenceClick = () => {
    setOpenReferenceModal((prev) => !prev);
  };

  const toolbarClickHandler = useCallback(
    (toolClicked: string) => {
      if (toolClicked === "addReference") {
        setOpenReferenceModal((prev) => !prev);
      } else if (toolClicked === "deleteReference") {
        const selectedRows = referenceGrid.current?.slickGrid.getSelectedRows() ?? [];
        const selectedRecord = selectedRows
          .map((data: number) => referenceGrid.current?.slickGrid.getDataItem(data))
          .filter((val): val is ReferenceMeasurement => Boolean(val))
          .map((val: ReferenceMeasurement) => val.id);
        setManualData((prev) => {
          const updatedData = { ...prev };
          updatedData.references = updatedData.references.filter(
            (val: CentralReferenceList) => !selectedRecord.includes(val.id),
          ) as typeof updatedData.references;
          if (!updatedData.references.length) {
            updatedData.value = null;
          }
          return updatedData;
        });
        setVariable((prev) => {
          const obj: { [id: string]: string } = { ...prev };
          Object.keys(obj).map((key: string) => {
            if (selectedRecord.includes(key)) {
              delete obj[key];
            }
          });
          return obj;
        });
        setToolButtonState((prev) => ({ ...prev, remove: true }));
      }
    },
    [setManualData],
  );

  const rawSelectionEvent = () => {
    const grid = referenceGrid.current;
    if (!grid) {
      return;
    }
    const totalRowSelected = grid.slickGrid.getSelectedRows();
    if (totalRowSelected.length > 0) {
      setToolButtonState((prev) => ({ ...prev, remove: false }));
    } else {
      setToolButtonState((prev) => ({ ...prev, remove: true }));
    }
  };

  const filesGridColumns = useMemo(
    () => [
      ...(source == manualCostSources.ADDITIONAL_COST
        ? [
            {
              field: "name",
              id: "name",
              name: t("Particular"),
              textAlign: "Left",
            },
            {
              field: "value",
              id: "value",
              name: t("Amount"),
              textAlign: "Left",
              disableMenuOptions: true,
              disableFiltering: true,
              disableSorting: true,
              _valueGetter: (params: { value: number }) =>
                getEstimateNumberValueBasedOnSettings(Number(params.value), estimateData, "amount"),
            },
          ]
        : [
            {
              field: "name",
              id: "name",
              name: t("Name"),
              textAlign: "Left",
            },
            {
              field: "measurement",
              id: "measurement",
              name: t("Measurement"),
              textAlign: "Left",
              disableMenuOptions: true,
              disableFiltering: true,
              disableSorting: true,
              formatter: MeasurementTemplate,
            },
          ]),
      {
        field: "variable",
        id: "variable",
        name: t("Variable"),
        textAlign: "Left",
        formatter: variableTemplate,
        disableMenuOptions: true,
        disableFiltering: true,
        disableSorting: true,
      },
    ],

    [],
  );

  // SlickGrid1 is a heavily-generic wrapper around slickgrid-react; the ref shape
  // (`SlickgridReactInstanceX<T>`) is internal and not exported, and `columnDefinitions`
  // requires a branded `ValidatedColumnDefinition<T>[]`. Bridging via `as never` keeps
  // the data flow correct without leaking `any` into our type surface.
  const refProp = referenceGrid as unknown as Parameters<typeof SlickGrid1<ReferenceMeasurement>>[0]["ref"];
  const columnDefsProp = filesGridColumns as unknown as Parameters<
    typeof SlickGrid1<ReferenceMeasurement>
  >[0]["columnDefinitions"];

  const getGrid = () => (
    <SlickGrid1<ReferenceMeasurement>
      ref={refProp}
      showPageCount={false}
      columnDefinitions={columnDefsProp}
      rowHeight={40}
      onSelectedRowIdsChanged={rawSelectionEvent}
      dataset={manualData.references as ReferenceMeasurement[]}
      toolbar={toolbarOptions}
      toolbarClickHandler={toolbarClickHandler}
      enableFilteringOption
      enableSortingOption
      hideNoDataIllustration
    />
  );

  const referencesData = () => {
    if (!manualData.references?.length) {
      return (
        <div className="flex gap-4 flex-col w-full items-center">
          <IconComponent name="InspectionFormNoData" width="13rem" height="13rem" />
          <div className="text-lg font-semibold text-text_gray">{noReferenceHeading1}</div>
          {noReferenceHeading2}
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleReferenceClick}>
            <Text>{t("Reference")}</Text>
          </ButtonComponent>
        </div>
      );
    } else {
      return (
        <div className="w-full flex flex-col gap-4">
          <div className="w-full h-60 overflow-auto flex">{getGrid()}</div>
          <div className="flex flex-col gap-2 w-full">
            <div className="flex gap-2 items-center">
              <Text>{t("Formula")}</Text>
              <IconComponent
                name="details"
                position="bottom"
                toolTipContent={formulaDetailToolTip}
                width="1.25rem"
                height="1.25rem"
              />
            </div>
            <Suspense fallback={<></>}>
              <FormulaBuilder
                variables={variable}
                references={manualData.references}
                initialData={initialData}
                setValueOfFormula={setValueOfFormula}
                source={source}
              />
            </Suspense>
          </div>
        </div>
      );
    }
  };

  const content = () => (
    <div className="flex flex-col gap-6">
      {upperContent && upperContent()}
      <div className="flex gap-2 items-center">
        <ToggleSwitchComponent
          checked={manualData.isReferenced}
          onChange={() => {
            setManualData((prev) => ({
              ...prev,
              value: null,
              isReferenced: !prev.isReferenced,
              references: [],
              formula: "",
            }));
            updateValueField && updateValueField(null);
          }}
        />
        <Text sx={{ whiteSpace: "normal" }} className="text-sm">
          {referenceToggleLabel}
        </Text>
      </div>
      {manualData.isReferenced && referencesData()}
    </div>
  );

  const rowSelected = () => {
    if (_.get(addReferenceRef, "current")) {
      const selectedRecords = addReferenceRef.current?.dataView
        .getAllSelectedItems()
        .filter((record: AddReferenceData) => record && !record.__isDummy) as AddReferenceData[];
      setAddReferenceButtonState(!!selectedRecords.length);
    }
  };

  const variableGenerator = (
    records: CentralReferenceList[],
    serial?: number,
  ): { serial: number; variableValue: string } => {
    if (serial === undefined) {
      const maxSerial = records.reduce((minVal: number, record: CentralReferenceList) => {
        return Math.max(record.serial ?? -1, minVal);
      }, -1);
      serial = maxSerial + 1;
    }

    const val = Math.floor(serial / 26);
    const remainder = serial % 26;

    let variableValue = "";
    if (val > 0) {
      variableValue += "A".repeat(val);
    }
    variableValue += String.fromCharCode(65 + remainder);

    if (records.some((measurement: CentralReferenceList) => measurement.variable == variableValue)) {
      return variableGenerator(records, serial + 1);
    } else {
      return { serial, variableValue };
    }
  };

  const generateAddData = (record: AddReferenceData, variable: string, serial: number): CentralReferenceList => {
    if (source == manualCostSources.MEASUREMENTS) {
      return {
        id: uuid(),
        name: record.name,
        measurement: getDisplayQuantity({
          quantity: record.quantity * record.multiplier,
          factor: record.unit.factor,
        }).quantity,
        variable,
        serial,
      };
    } else {
      return {
        id: uuid(),
        name: record.name,
        value: record.value!,
        variable,
        serial,
      };
    }
  };

  const generateAndFetchLatestData = (newRecords: AddReferenceData[]) => {
    const dataObj: CentralReferenceList[] = [...manualData.references];
    const varibaleDataObj: { [id: string]: string } = { ...variable };
    let latestSerialVal;

    for (let record of newRecords) {
      const { serial, variableValue } = variableGenerator(dataObj, latestSerialVal);
      latestSerialVal = serial + 1;

      const data = generateAddData(record, variableValue, serial);
      dataObj.push(data);
      varibaleDataObj[data.id] = variableValue;
    }
    setManualData((prev) => {
      const updatedData = { ...prev };
      updatedData.references = dataObj as typeof updatedData.references;
      return updatedData;
    });
    setVariable(varibaleDataObj);
  };

  const handleActionClick = async () => {
    setIsAnyActiveAction(true);
    if (openReferenceModal) {
      const selectedRecords = addReferenceRef.current?.dataView
        .getAllSelectedItems()
        .filter((record: AddReferenceData) => record && !record.__isDummy) as AddReferenceData[];
      generateAndFetchLatestData(selectedRecords);
      handleReferenceClick();
    } else {
      await handleSave();
    }
    setIsAnyActiveAction(false);
  };

  const validateVariables = (data: CentralManualCostState) => {
    if (data.isReferenced && data.references.length) {
      if (data.references.some((val: CentralReferenceList) => !val.variable)) {
        return false;
      }

      const variablesData = [...new Set(Object.values(variable))];
      if (variablesData.length !== data.references.length) {
        return false;
      }
    }
    return true;
  };

  const footer = (isReferenceModal?: boolean) => (
    <div className="flex gap-4 ">
      <ButtonComponent
        disabled={isReferenceModal ? !addReferenceButtonState : isSaveButtonDisabled || !validateVariables(manualData)}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleActionClick}
        loading={isAnyActiveAction}
      >
        {isReferenceModal ? t("Add") : footerConfirmText}
      </ButtonComponent>
      <ButtonComponent
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        onClick={isReferenceModal ? handleReferenceClick : handleCancel}
        disabled={isAnyActiveAction}
      >
        <Text>{footerCancelText}</Text>
      </ButtonComponent>
    </div>
  );

  const modals = (isReferenceModal?: boolean) => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={footer(isReferenceModal)}
      width={isReferenceModal ? "55%" : "60%"}
      headerTitle={isReferenceModal ? addReferenceHeaderText : headerText}
      onCancel={isAnyActiveAction ? undefined : isReferenceModal ? handleReferenceClick : handleCancel}
      isVisible
      modelContent={
        isReferenceModal ? (
          <AddReferences
            ref={addReferenceRef}
            source={source}
            estimateId={estimateId}
            fileVersionId={fileVersionId}
            rowSelected={rowSelected}
          />
        ) : (
          content()
        )
      }
    />
  );

  return (
    <div>
      {modals()}
      {openReferenceModal && modals(openReferenceModal)}
    </div>
  );
}

export default CentralManualCostComponent;
